import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';

interface EditValidationErrorDialogProps {
  open: boolean;
  onClose: () => void;
  errors: string[];
  recordId?: number;
  recordInfo?: string;
}

const EditValidationErrorDialog: React.FC<EditValidationErrorDialogProps> = ({
  open,
  onClose,
  errors,
  recordId,
  recordInfo
}) => {
  const { t } = useTranslation();

  const formatErrorMessage = (errorMessage: string) => {
    // 將分號分隔的錯誤信息轉換為數組
    return errorMessage.split(';').map((error, index) => (
      <Box key={index} sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="error" sx={{ fontSize: '0.875rem' }}>
          • {error.trim()}
        </Typography>
      </Box>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '50vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          <Typography variant="h6" component="div">
            {t('pages.siteSlump.editValidationError.title')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* 錯誤摘要 */}
        <Box sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            {t('pages.siteSlump.editValidationError.summary')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Chip
              label={t('pages.siteSlump.editValidationError.validationFailed')}
              color="error"
              variant="outlined"
              sx={{ color: 'error.contrastText', borderColor: 'error.contrastText' }}
            />
            {recordId && (
              <Chip
                label={`${t('pages.siteSlump.editValidationError.recordId')}: ${recordId}`}
                color="info"
                variant="outlined"
                sx={{ color: 'error.contrastText', borderColor: 'error.contrastText' }}
              />
            )}
          </Box>
          {recordInfo && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {recordInfo}
            </Typography>
          )}
        </Box>

        {/* 錯誤詳情 */}
        <Paper sx={{ m: 2, maxHeight: '40vh', overflow: 'auto' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="error" />
              {t('pages.siteSlump.editValidationError.errorDetails')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              {errors.map((error, index) => (
                <ListItem key={index} sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" color="error">
                        {error.trim()}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Paper>

        {/* 解決建議 */}
        <Box sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            {t('pages.siteSlump.editValidationError.suggestions')}
          </Typography>
          <List dense sx={{ py: 0 }}>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {t('pages.siteSlump.editValidationError.suggestion1')}
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {t('pages.siteSlump.editValidationError.suggestion2')}
                  </Typography>
                }
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {t('pages.siteSlump.editValidationError.suggestion3')}
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          startIcon={<CloseIcon />}
        >
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditValidationErrorDialog;
